import { useState } from 'react';
import { Leaf, Home, Maximize2, MapPin, Award, ChevronRight, ChevronLeft, Check } from 'lucide-react';

interface OnboardingData {
  interests: string[];
  growingMethod: string;
  space: string;
  zone: string;
  experience: string;
}

interface OnboardingWizardProps {
  onComplete: (data: OnboardingData) => void;
  onSkip: () => void;
}

const steps = [
  { id: 'interests', title: 'What do you want to grow?', icon: Leaf },
  { id: 'method', title: 'How will you grow?', icon: Home },
  { id: 'space', title: 'How much space?', icon: Maximize2 },
  { id: 'zone', title: 'Your growing zone', icon: MapPin },
  { id: 'experience', title: 'Your experience', icon: Award },
];

const interestOptions = [
  { id: 'vegetables', label: 'Vegetables', emoji: '🥕', desc: 'Tomatoes, peppers, lettuce, carrots' },
  { id: 'herbs', label: 'Herbs', emoji: '🌿', desc: 'Basil, cilantro, rosemary, mint' },
  { id: 'fruits', label: 'Fruits', emoji: '🍓', desc: 'Berries, apples, citrus, melons' },
  { id: 'flowers', label: 'Flowers', emoji: '🌻', desc: 'Sunflowers, pollinator plants, ornamentals' },
  { id: 'surprise', label: 'Surprise Me!', emoji: '🎁', desc: 'Show me a bit of everything' },
];

const methodOptions = [
  { id: 'raised', label: 'Raised Beds', emoji: '🔲', desc: 'Elevated garden boxes' },
  { id: 'ground', label: 'In-Ground', emoji: '🌱', desc: 'Traditional garden plots' },
  { id: 'containers', label: 'Containers', emoji: '🪴', desc: 'Pots, planters, balcony gardens' },
  { id: 'vertical', label: 'Vertical/Greenhouse', emoji: '🏗️', desc: 'Towers, walls, or indoor growing' },
  { id: 'unsure', label: 'Not Sure Yet', emoji: '🤔', desc: 'Help me decide later' },
];

const spaceOptions = [
  { id: 'small', label: 'Small', emoji: '🏢', desc: 'Balcony, patio, or windowsill' },
  { id: 'medium', label: 'Medium', emoji: '🏠', desc: 'Suburban yard or small plot' },
  { id: 'large', label: 'Large', emoji: '🏡', desc: '1/4 acre or more' },
  { id: 'unsure', label: 'Not Sure', emoji: '📏', desc: 'I\'ll measure later' },
];

const experienceOptions = [
  { id: 'novice', label: 'First Garden', emoji: '🌱', desc: 'Start with 12 easy plants', color: 'bg-green-100 border-green-300' },
  { id: 'intermediate', label: 'Some Experience', emoji: '🌿', desc: 'Curated collections (50-100 plants)', color: 'bg-blue-100 border-blue-300' },
  { id: 'expert', label: 'Expert Gardener', emoji: '🌳', desc: 'Full access to 12,000+ varieties', color: 'bg-purple-100 border-purple-300' },
];

export function OnboardingWizard({ onComplete, onSkip }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<OnboardingData>({
    interests: [],
    growingMethod: '',
    space: '',
    zone: '',
    experience: '',
  });
  const [zipCode, setZipCode] = useState('');

  const updateData = (key: keyof OnboardingData, value: string | string[]) => {
    setData(prev => ({ ...prev, [key]: value }));
  };

  const toggleInterest = (interest: string) => {
    if (interest === 'surprise') {
      updateData('interests', ['vegetables', 'herbs', 'fruits', 'flowers']);
      return;
    }
    const current = data.interests;
    if (current.includes(interest)) {
      updateData('interests', current.filter(i => i !== interest));
    } else {
      updateData('interests', [...current, interest]);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onComplete(data);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return data.interests.length > 0;
      case 1: return data.growingMethod !== '';
      case 2: return data.space !== '';
      case 3: return data.zone !== '' || zipCode.length === 5;
      case 4: return data.experience !== '';
      default: return true;
    }
  };

  const detectZone = async () => {
    if (zipCode.length === 5) {
      try {
        const response = await fetch(`https://phzmapi.org/${zipCode}.json`);
        const zoneData = await response.json();
        updateData('zone', zoneData.zone);
      } catch {
        updateData('zone', '7');
      }
    }
  };

  const CurrentIcon = steps[currentStep].icon;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CurrentIcon className="w-8 h-8" />
              <h2 className="text-2xl font-bold">{steps[currentStep].title}</h2>
            </div>
            <button onClick={onSkip} className="text-white/80 hover:text-white text-sm">
              Skip
            </button>
          </div>
          
          {/* Progress */}
          <div className="flex gap-2 mt-4">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                className={`h-2 flex-1 rounded-full transition-all ${
                  idx <= currentStep ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Interests */}
          {currentStep === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {interestOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => toggleInterest(option.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    data.interests.includes(option.id) || (option.id === 'surprise' && data.interests.length >= 4)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="text-3xl mb-2">{option.emoji}</div>
                  <div className="font-semibold text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-600">{option.desc}</div>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Growing Method */}
          {currentStep === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {methodOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => updateData('growingMethod', option.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    data.growingMethod === option.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="text-3xl mb-2">{option.emoji}</div>
                  <div className="font-semibold text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-600">{option.desc}</div>
                </button>
              ))}
            </div>
          )}

          {/* Step 3: Space */}
          {currentStep === 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {spaceOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => updateData('space', option.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    data.space === option.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="text-3xl mb-2">{option.emoji}</div>
                  <div className="font-semibold text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-600">{option.desc}</div>
                </button>
              ))}
            </div>
          )}

          {/* Step 4: Zone */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-gray-600 mb-4">Enter your zip code and we'll detect your hardiness zone</p>
                <div className="flex gap-2 justify-center">
                  <input
                    type="text"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="12345"
                    className="px-4 py-3 border-2 border-gray-300 rounded-lg text-center text-xl w-32 focus:border-green-500 focus:outline-none"
                  />
                  <button
                    onClick={detectZone}
                    disabled={zipCode.length !== 5}
                    className="px-4 py-3 bg-green-600 text-white rounded-lg disabled:bg-gray-300 hover:bg-green-700 transition-colors"
                  >
                    Detect
                  </button>
                </div>
              </div>
              
              {data.zone && (
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <div className="text-4xl mb-2">🌡️</div>
                  <div className="text-2xl font-bold text-green-800">Zone {data.zone}</div>
                  <div className="text-green-600">Perfect! We'll show plants that thrive in your area.</div>
                </div>
              )}
              
              <div className="text-center">
                <button
                  onClick={() => updateData('zone', '7')}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Skip and use Zone 7 (default)
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Experience */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <p className="text-gray-600 text-center mb-4">Choose your experience level - you can change this anytime!</p>
              {experienceOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => updateData('experience', option.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                    data.experience === option.id
                      ? option.color
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="text-4xl">{option.emoji}</div>
                  <div className="flex-1">
                    <div className="font-bold text-lg text-gray-900">{option.label}</div>
                    <div className="text-gray-600">{option.desc}</div>
                  </div>
                  {data.experience === option.id && <Check className="w-6 h-6 text-green-600" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-6 py-3 rounded-lg text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
          
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {currentStep === steps.length - 1 ? 'Get Started!' : 'Next'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
